/**
 * CSP-safe blob to canvas rendering utilities
 * Avoids using blob URLs entirely
 */

/**
 * Converts a Blob to ImageData without using URLs
 * This avoids CSP issues by reading the blob data directly
 */
export async function blobToImageData(blob: Blob): Promise<{
  imageData: ImageData;
  width: number;
  height: number;
}> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        
        // Create a bitmap from the array buffer
        const imageBitmap = await createImageBitmap(new Blob([arrayBuffer]));
        
        // Create an OffscreenCanvas to extract ImageData
        const canvas = new OffscreenCanvas(imageBitmap.width, imageBitmap.height);
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Failed to get context');
        
        // Draw the bitmap
        ctx.drawImage(imageBitmap, 0, 0);
        
        // Get ImageData
        const imageData = ctx.getImageData(0, 0, imageBitmap.width, imageBitmap.height);
        
        // Clean up
        imageBitmap.close();
        
        resolve({
          imageData,
          width: imageBitmap.width,
          height: imageBitmap.height
        });
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read blob'));
    reader.readAsArrayBuffer(blob);
  });
}

/**
 * Renders ImageData to a canvas with scaling
 */
export function renderImageDataToCanvas(
  imageData: ImageData,
  canvas: HTMLCanvasElement,
  maxWidth: number = 800,
  maxHeight: number = 800
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');
  
  // Calculate scaled dimensions
  const scale = Math.min(
    1,
    maxWidth / imageData.width,
    maxHeight / imageData.height
  );
  
  const scaledWidth = Math.round(imageData.width * scale);
  const scaledHeight = Math.round(imageData.height * scale);
  
  // Set canvas size
  canvas.width = scaledWidth;
  canvas.height = scaledHeight;
  
  // Create temporary canvas with original size
  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = imageData.width;
  tempCanvas.height = imageData.height;
  const tempCtx = tempCanvas.getContext('2d');
  if (!tempCtx) throw new Error('Failed to get temp context');
  
  // Put image data on temp canvas
  tempCtx.putImageData(imageData, 0, 0);
  
  // Draw scaled version on main canvas
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(tempCanvas, 0, 0, scaledWidth, scaledHeight);
}

/**
 * Direct blob to canvas rendering without URLs
 */
export async function renderBlobToCanvasNoURL(
  blob: Blob,
  canvas: HTMLCanvasElement,
  options: {
    maxWidth?: number;
    maxHeight?: number;
  } = {}
): Promise<void> {
  const { maxWidth = 800, maxHeight = 800 } = options;
  
  try {
    // Try createImageBitmap first (most efficient, no URLs)
    if ('createImageBitmap' in window) {
      const imageBitmap = await createImageBitmap(blob);
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Failed to get canvas context');
      
      // Calculate dimensions
      const scale = Math.min(
        1,
        maxWidth / imageBitmap.width,
        maxHeight / imageBitmap.height
      );
      
      const width = Math.round(imageBitmap.width * scale);
      const height = Math.round(imageBitmap.height * scale);
      
      canvas.width = width;
      canvas.height = height;
      
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(imageBitmap, 0, 0, width, height);
      
      imageBitmap.close();
    } else {
      // Fallback to ImageData method
      const { imageData, width: origWidth, height: origHeight } = await blobToImageData(blob);
      renderImageDataToCanvas(imageData, canvas, maxWidth, maxHeight);
    }
  } catch (error) {
    console.error('renderBlobToCanvasNoURL error:', error);
    throw error;
  }
}